'use client';

import { c as _c } from "react/compiler-runtime";
import { textInputClassName } from '@/components/ui/field';
import type { HofDashboardEntry } from '@/features/hof-dashboard/api/hof-dashboard-data';
import { SECONDARY_BUTTON_CLASS } from '@/features/hof-dashboard/components/dashboard-ui';
import { HOF_ENTRY_STATUS_LABELS, HOF_REVIEW_CHOICES, type HofEntryStatus } from '@/features/hof-dashboard/constants';
import { useAutosaveReview, type AutosaveState } from '@/features/hof-dashboard/hooks/use-autosave-review';
import { useWarnBeforeLeaving } from '@/features/hof-dashboard/hooks/use-warn-before-leaving';
import { translate, type TextKey } from '@/features/hof-dashboard/texts';
import { notifyFailure } from '@/features/hof-dashboard/utils/notify-failure';
import { trpc } from '@/trpc/client';
import type { Locale } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import { Radio, RadioGroup } from '@headlessui/react';
import { Check, CloudOff, Send } from 'lucide-react';
import type React from 'react';
import { useId, useState } from 'react';

/**
 * A comment of the Ressort to the Hof, sent with a button rather than saved as it is typed: one
 * comment is one step of the history, never a half-written one. The same comment twice in a
 * row is no new step, so it cannot be sent again.
 */
const CommentComposer: React.FC<{
  entry: HofDashboardEntry;
  hofId: string;
  locale: Locale;
}> = t0 => {
  const $ = _c(33);
  const {
    entry,
    hofId,
    locale
  } = t0;
  const utils = trpc.useUtils();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      networkMode: "always"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const mutation = trpc.hofDashboard.addSubmissionComment.useMutation(t1);
  const [text, setText] = useState("");
  const commentId = useId();
  let t2;
  if ($[1] !== text) {
    t2 = text.trim();
    $[1] = text;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const comment = t2;
  const latest = entry.reviewLog.findLast(_temp)?.comment;
  useWarnBeforeLeaving(comment !== "");
  let t3;
  if ($[3] !== comment || $[4] !== entry.id || $[5] !== hofId || $[6] !== locale || $[7] !== mutation || $[8] !== utils) {
    t3 = async () => {
      try {
        await mutation.mutateAsync({
          hofId,
          submissionId: entry.id,
          comment
        });
        setText("");
        await utils.hofDashboard.getHofDashboard.invalidate({
          hofId
        });
      } catch {
        notifyFailure(locale, "commentFailed");
      }
    };
    $[3] = comment;
    $[4] = entry.id;
    $[5] = hofId;
    $[6] = locale;
    $[7] = mutation;
    $[8] = utils;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const send = t3;
  let t4;
  if ($[10] !== locale) {
    t4 = translate("commentLabel", locale);
    $[10] = locale;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== commentId || $[13] !== t4) {
    t5 = <label htmlFor={commentId} className="font-body block text-sm font-medium text-gray-500">{t4}</label>;
    $[12] = commentId;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = event => setText(event.target.value);
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== commentId || $[17] !== text) {
    t7 = <textarea id={commentId} rows={3} maxLength={5000} value={text} onChange={t6} className={textInputClassName} />;
    $[16] = commentId;
    $[17] = text;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const t8 = comment === "" || comment === latest || mutation.isPending;
  let t9;
  if ($[19] !== send) {
    t9 = () => void send();
    $[19] = send;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Send aria-hidden={true} />;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const t11 = mutation.isPending ? "saving" : "sendComment";
  let t12;
  if ($[22] !== locale || $[23] !== t11) {
    t12 = translate(t11, locale);
    $[22] = locale;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12 || $[26] !== t8 || $[27] !== t9) {
    t13 = <button type="button" className={SECONDARY_BUTTON_CLASS} disabled={t8} onClick={t9}>{t10}{t12}</button>;
    $[25] = t12;
    $[26] = t8;
    $[27] = t9;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t13 || $[30] !== t5 || $[31] !== t7) {
    t14 = <div className="space-y-2">{t5}{t7}{t13}</div>;
    $[29] = t13;
    $[30] = t5;
    $[31] = t7;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  return t14;
};

/** "Eingereicht" first: no status of the Ressort yet, the way a new submission starts. */
const CHOICES: HofEntryStatus[] = ['submitted', ...HOF_REVIEW_CHOICES];
const AUTOSAVE_LABEL: Record<Exclude<AutosaveState, 'idle'>, TextKey> = {
  saving: 'saving',
  saved: 'saved',
  offline: 'autosaveOffline',
  error: 'autosaveError'
};

/** Where the autosave stands, as a small pill beside the title; nothing before the first edit. */
const AutosavePill: React.FC<{
  state: AutosaveState;
  locale: Locale;
  onRetry: () => void;
}> = t0 => {
  const $ = _c(22);
  const {
    state,
    locale,
    onRetry
  } = t0;
  if (state === "idle") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span aria-live="polite" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const failed = state === "error" || state === "offline";
  const t1 = failed ? "bg-amber-50 text-amber-800 ring-amber-200" : "bg-gray-100 text-gray-600 ring-gray-200";
  let t2;
  if ($[1] !== t1) {
    t2 = cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset", t1);
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== state) {
    t3 = state === "saved" && <Check className="h-3.5 w-3.5" aria-hidden={true} />;
    $[3] = state;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== failed) {
    t4 = failed && <CloudOff className="h-3.5 w-3.5" aria-hidden={true} />;
    $[5] = failed;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = AUTOSAVE_LABEL[state];
  let t6;
  if ($[7] !== locale || $[8] !== t5) {
    t6 = translate(t5, locale);
    $[7] = locale;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4 || $[13] !== t6) {
    t7 = <span className={t2}>{t3}{t4}{t6}</span>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== locale || $[16] !== onRetry || $[17] !== state) {
    t8 = state === "error" && <button type="button" onClick={onRetry} className="text-conveniat-green min-h-10 cursor-pointer text-xs font-semibold hover:underline">{translate("retry", locale)}</button>;
    $[15] = locale;
    $[16] = onRetry;
    $[17] = state;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t7 || $[20] !== t8) {
    t9 = <span aria-live="polite" className="flex items-center gap-2">{t7}{t8}</span>;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
};

/**
 * Where a reviewer answers a submission: its status, as the option cards of the site's forms,
 * saved at once without a button, and a comment to the Hof, sent with one. Both land in the
 * history above, with the reviewer's name and the time.
 */
export const ReviewPanel: React.FC<{
  entry: HofDashboardEntry;
  hofId: string;
  /** For the version that counts of a form of versions: it can be made the last one. */
  canFinalize: boolean;
  locale: Locale;
}> = t0 => {
  const $ = _c(51);
  const {
    entry,
    hofId,
    canFinalize,
    locale
  } = t0;
  let t1;
  if ($[0] !== entry.final || $[1] !== entry.reviewStatus) {
    t1 = {
      status: entry.reviewStatus,
      final: entry.final
    };
    $[0] = entry.final;
    $[1] = entry.reviewStatus;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    values,
    setStatus,
    setFinal,
    state,
    retry
  } = useAutosaveReview(hofId, entry.id, t1);
  const finalId = useId();
  const statusId = useId();
  let t2;
  if ($[3] !== locale) {
    t2 = translate("reviewTitle", locale);
    $[3] = locale;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <p className="text-sm font-semibold text-gray-900">{t2}</p>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== locale || $[8] !== retry || $[9] !== state) {
    t4 = <AutosavePill state={state} locale={locale} onRetry={retry} />;
    $[7] = locale;
    $[8] = retry;
    $[9] = state;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <div className="flex flex-wrap items-center justify-between gap-2">{t3}{t4}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const t6 = values.status ?? "submitted";
  let t7;
  if ($[14] !== setStatus) {
    t7 = choice => setStatus(choice === "submitted" ? undefined : choice);
    $[14] = setStatus;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== locale) {
    t8 = translate("reviewStatus", locale);
    $[16] = locale;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== statusId || $[19] !== t8) {
    t9 = <p id={statusId} className="font-body mb-2 text-sm font-medium text-gray-500">{t8}</p>;
    $[18] = statusId;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== locale) {
    t10 = CHOICES.map(choice_0 => <Radio key={choice_0} value={choice_0} className="font-body flex min-h-10 cursor-pointer items-center justify-center rounded-lg border-2 border-gray-200 bg-white px-3 py-2 text-center text-sm font-medium text-gray-700 transition-all duration-200 hover:border-gray-300 data-checked:border-green-600 data-checked:bg-green-50 data-checked:text-green-700 data-focus:ring-2 data-focus:ring-green-600 data-focus:ring-offset-2 data-focus:outline-none">{HOF_ENTRY_STATUS_LABELS[choice_0][locale]}</Radio>);
    $[21] = locale;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10) {
    t11 = <div className="grid grid-cols-2 gap-2 @3xl:grid-cols-4">{t10}</div>;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== statusId || $[26] !== t11 || $[27] !== t6 || $[28] !== t7 || $[29] !== t9) {
    t12 = <RadioGroup value={t6} onChange={t7} aria-labelledby={statusId}>{t9}{t11}</RadioGroup>;
    $[25] = statusId;
    $[26] = t11;
    $[27] = t6;
    $[28] = t7;
    $[29] = t9;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== canFinalize || $[32] !== finalId || $[33] !== locale || $[34] !== setFinal || $[35] !== values.final) {
    t13 = canFinalize && <div className="flex items-start gap-3"><input id={finalId} type="checkbox" checked={values.final} onChange={event => setFinal(event.target.checked)} className="text-conveniat-green focus:ring-conveniat-green mt-[3px] h-4 w-4 rounded border-0 bg-green-100 shadow-sm ring-1 ring-transparent ring-inset focus:ring-2 focus:ring-offset-0 focus:outline-none" /><label htmlFor={finalId} className="font-body text-sm text-gray-700"><span className="font-medium text-gray-900">{translate("finalLabel", locale)}</span><span className="block text-xs text-gray-500">{translate("finalHint", locale)}</span></label></div>;
    $[31] = canFinalize;
    $[32] = finalId;
    $[33] = locale;
    $[34] = setFinal;
    $[35] = values.final;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== entry || $[38] !== hofId || $[39] !== locale) {
    t14 = <CommentComposer entry={entry} hofId={hofId} locale={locale} />;
    $[37] = entry;
    $[38] = hofId;
    $[39] = locale;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== locale) {
    t15 = translate("reviewHint", locale);
    $[41] = locale;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t15) {
    t16 = <p className="text-xs text-gray-500">{t15}</p>;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t12 || $[46] !== t13 || $[47] !== t14 || $[48] !== t16 || $[49] !== t5) {
    t17 = <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">{t5}{t12}{t13}{t14}{t16}</div>;
    $[45] = t12;
    $[46] = t13;
    $[47] = t14;
    $[48] = t16;
    $[49] = t5;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  return t17;
};
function _temp(change) {
  return change.comment !== undefined;
}